// #1090: sayfalar route bazında tembel yüklenir; ilk açılışta yalnız gidilen sayfanın kodu iner.
// Barrel (`@/pages`) bilerek kullanılmaz: tek bir statik içe aktarma bütün sayfaları ana pakete geri sokar.
// Her sayfanın yükleyicisi bir kez tanımlanır; hem `lazy()` hem `preloadPage()` (üzerine gelince önden yükleme) kullanır.
import { createElement, lazy, type ComponentType } from 'react'

import { reloadForMissingChunk } from '@/lib/chunk-reload-guard'
import { ALIAS_ROUTES, type AliasRoute } from '@/pages/alias-routes'

type PageModule = Record<string, unknown>
type Loader = () => Promise<PageModule>

/**
 * `reloadOnError` verilirse (gerçek gezinme) yükleme hatasında önce sayfa yenilemesi denenir: yenilendiyse yedek ekranda
 * beklenir (hata ekranı yok), yenilenemediyse hata yükselir. Önden yüklemede verilmez: hata çağırana döner, sayfa yenilenmez.
 */
export const pickPage =
  (load: Loader, key: string, reloadOnError?: () => boolean) => async () => {
    let mod: PageModule
    try {
      mod = await load()
    } catch (err) {
      if (reloadOnError?.()) return new Promise<{ default: ComponentType }>(() => undefined)
      throw err
    }
    return { default: mod[key] as ComponentType }
  }

// Kod daha önce indiyse (önden yükleme / açılış adresi / ilk gezinme) sayfa EŞZAMANLI çizilir. Düz `lazy()` her ilk çizimde bir
// kez askıya alır: yedek ekran basılır ve React içeriği yedekten sonra ~300 ms geciktirir (hazır HTML'li sayfalarda boş an).
const warmers = new Map<Loader, Array<() => Promise<void>>>()

export function lazyPage<P extends object = object>(load: Loader, key: string): ComponentType<P> {
  let ready: ComponentType | null = null
  const Lazy = lazy(async () => {
    const loaded = await pickPage(load, key, () => reloadForMissingChunk())()
    ready = loaded.default
    return loaded
  })
  const warm = async () => {
    if (ready) return
    const loaded = await pickPage(load, key)()
    ready = loaded.default
  }
  warmers.set(load, [...(warmers.get(load) ?? []), warm])
  return function Page(props: P) {
    return ready ? createElement(ready as ComponentType<P>, props) : createElement(Lazy as ComponentType<P>, props)
  }
}

/** Yükleyiciyi çalıştırır; ilgili sayfaları eşzamanlı çizime hazırlar. Hata çağırana döner. */
export async function warmUp(load: Loader): Promise<void> {
  const list = warmers.get(load)
  if (!list) {
    await load()
    return
  }
  await Promise.all(list.map((warm) => warm()))
}

const loadLandingPage = () => import('@/pages/landing')
const loadAuthPage = () => import('@/pages/login')
const loadDashboardPage = () => import('@/pages/dashboard')
const loadRankTrackerPage = () => import('@/pages/rank-tracker')
const loadDeepsearchPage = () => import('@/pages/deepsearch')
const loadFreqFinderPage = () => import('@/pages/freqfinder')
const loadSettingsPage = () => import('@/pages/settings')
const loadProfilePage = () => import('@/pages/profile')
const loadResetPasswordPage = () => import('@/pages/reset-password')
const loadAuthCallbackPage = () => import('@/pages/auth-callback')
const loadUnauthorizedPage = () => import('@/pages/unauthorized')
const loadNewReleasesPage = () => import('@/pages/new-releases')
const loadSalesEstimatorPage = () => import('@/pages/sales-estimator')
const loadKeyInsightPage = () => import('@/pages/key-insight')
const loadProductDatabasePage = () => import('@/pages/product-database')
const loadSearchVolumePage = () => import('@/pages/sv')
const loadAdminAllowedEmailsPage = () => import('@/pages/admin/allowed-emails')
const loadAnchorReviewPage = () => import('@/pages/admin/anchor-review')
const loadUserActivityPage = () => import('@/pages/admin/user-activity')
const loadAdminAcademyPage = () => import('@/pages/admin/academy')
const loadAdminContentManagementPage = () => import('@/pages/admin/content-management')
const loadAdminBlogManagementPage = () => import('@/pages/admin/blog-management')
const loadAdminNotificationsPage = () => import('@/pages/admin/notifications')
const loadAdminScrapingGatePage = () => import('@/pages/admin/scraping-gate')
const loadAdminKeywordScoreLabPage = () => import('@/pages/admin/keyword-score-lab')
const loadAdminExtensionFeaturesPage = () => import('@/pages/admin/extension-features')
const loadAboutPage = () => import('@/pages/about')
const loadBlogComingSoonPage = () => import('@/pages/blog-coming-soon')
const loadContactPage = () => import('@/pages/contact')
const loadPrivacyPolicyPage = () => import('@/pages/privacy-policy')
const loadTermsPage = () => import('@/pages/terms')
const loadPricingPage = () => import('@/pages/pricing')
const loadCheckoutPage = () => import('@/pages/checkout')
const loadOrderDetailPage = () => import('@/pages/order-detail')
const loadProfitCalculatorPage = () => import('@/pages/profit-calculator')
const loadKeywordListsPage = () => import('@/pages/keyword-lists')
const loadListingBuilderPage = () => import('@/pages/listing-builder')
const loadAmazonDashboardPage = () => import('@/pages/amazon/dashboard')
const loadAmazonOAuthCallbackPage = () => import('@/pages/amazon-callback')
const loadBillingPage = () => import('@/pages/billing')
const loadAcademyPage = () => import('@/pages/academy')
const loadVideoDetailPage = () => import('@/pages/academy/video-detail')
const loadSQPPage = () => import('@/pages/amazon/sqp')
const loadAdvertisingPage = () => import('@/pages/amazon/advertising')
const loadProductDetailPage = () => import('@/pages/amazon/product-detail')
const loadProductListPage = () => import('@/pages/product-list')

export const LandingPage = lazyPage(loadLandingPage, 'LandingPage')
export const AuthPage = lazyPage(loadAuthPage, 'AuthPage')
export const DashboardPage = lazyPage(loadDashboardPage, 'DashboardPage')
export const RankTrackerPage = lazyPage(loadRankTrackerPage, 'RankTrackerPage')
export const DeepsearchPage = lazyPage(loadDeepsearchPage, 'DeepsearchPage')
export const FreqFinderPage = lazyPage(loadFreqFinderPage, 'FreqFinderPage')
export const SettingsPage = lazyPage(loadSettingsPage, 'SettingsPage')
export const ProfilePage = lazyPage(loadProfilePage, 'ProfilePage')
export const ResetPasswordPage = lazyPage(loadResetPasswordPage, 'ResetPasswordPage')
export const AuthCallbackPage = lazyPage(loadAuthCallbackPage, 'AuthCallbackPage')
export const UnauthorizedPage = lazyPage(loadUnauthorizedPage, 'UnauthorizedPage')
export const NewReleasesPage = lazyPage(loadNewReleasesPage, 'NewReleasesPage')
export const SalesEstimatorPage = lazyPage(loadSalesEstimatorPage, 'SalesEstimatorPage')
export const KeyInsightPage = lazyPage(loadKeyInsightPage, 'default')
export const ProductDatabasePage = lazyPage(loadProductDatabasePage, 'default')
export const SearchVolumePage = lazyPage(loadSearchVolumePage, 'default')
export const AdminAllowedEmailsPage = lazyPage(loadAdminAllowedEmailsPage, 'AdminAllowedEmailsPage')
export const AnchorReviewPage = lazyPage(loadAnchorReviewPage, 'AnchorReviewPage')
export const UserActivityPage = lazyPage(loadUserActivityPage, 'UserActivityPage')
export const AdminAcademyPage = lazyPage(loadAdminAcademyPage, 'AdminAcademyPage')
export const AdminContentManagementPage = lazyPage(loadAdminContentManagementPage, 'ContentManagementPage')
export const AdminBlogManagementPage = lazyPage(loadAdminBlogManagementPage, 'BlogManagementPage')
export const AdminNotificationsPage = lazyPage(loadAdminNotificationsPage, 'AdminNotificationsPage')
export const AdminScrapingGatePage = lazyPage(loadAdminScrapingGatePage, 'ScrapingGatePage')
export const AdminKeywordScoreLabPage = lazyPage(loadAdminKeywordScoreLabPage, 'KeywordScoreLabPage')
export const AdminExtensionFeaturesPage = lazyPage(loadAdminExtensionFeaturesPage, 'ExtensionFeaturesPage')
export const AboutPage = lazyPage(loadAboutPage, 'AboutPage')
export const BlogComingSoonPage = lazyPage(loadBlogComingSoonPage, 'BlogComingSoonPage')
export const ContactPage = lazyPage(loadContactPage, 'ContactPage')
export const PrivacyPolicyPage = lazyPage(loadPrivacyPolicyPage, 'PrivacyPolicyPage')
export const TermsPage = lazyPage(loadTermsPage, 'TermsPage')
export const PricingPage = lazyPage(loadPricingPage, 'PricingPage')
export const CheckoutPage = lazyPage(loadCheckoutPage, 'CheckoutPage')
export const OrderDetailPage = lazyPage(loadOrderDetailPage, 'OrderDetailPage')
export const ProfitCalculatorPage = lazyPage(loadProfitCalculatorPage, 'ProfitCalculatorPage')
export const KeywordListsPage = lazyPage(loadKeywordListsPage, 'KeywordListsPage')
export const ListingBuilderPage = lazyPage(loadListingBuilderPage, 'ListingBuilderPage')
export const AmazonDashboardPage = lazyPage(loadAmazonDashboardPage, 'AmazonDashboardPage')
export const AmazonOAuthCallbackPage = lazyPage(loadAmazonOAuthCallbackPage, 'AmazonCallbackPage')
export const BillingPage = lazyPage(loadBillingPage, 'BillingPage')
export const AcademyPage = lazyPage(loadAcademyPage, 'AcademyPage')
export const VideoDetailPage = lazyPage(loadVideoDetailPage, 'VideoDetailPage')
export const SQPPage = lazyPage(loadSQPPage, 'SQPPage')
export const AdvertisingPage = lazyPage(loadAdvertisingPage, 'AdvertisingPage')
export const ProductDetailPage = lazyPage(loadProductDetailPage, 'ProductDetailPage')
export const ProductListPage = lazyPage(loadProductListPage, 'ProductListPage')

const loadKaiPage = () => import('@/pages/kai/kai-page')
// Kai sayfası ve `socket.io-client` ana pakete girmez; `@/pages` barrel'ına EKLENMEZ.
export const KaiPage = lazyPage(loadKaiPage, 'default')

// Alias sayfaları (landing/gizlilik kopyaları) props alır: rota verisi `@/pages/alias-routes`ta, sayfa kodu burada tembel.
type AliasPageProps = { route: AliasRoute }
const loadAliasPage = () => import('@/pages/public-aliases')
export const AliasPage = lazyPage<AliasPageProps>(loadAliasPage, 'AliasPage')

/** Route yolundan sayfa yükleyicisi: kenar çubuğunda üzerine gelince/odaklanınca önden indirmek için. */
const loaders: Record<string, Loader> = {
  '/app/chatbot': loadKaiPage,
  '/app/dashboard': loadDashboardPage,
  '/app/rank-tracker': loadRankTrackerPage,
  '/app/deepsearch': loadDeepsearchPage,
  '/app/freqfinder': loadFreqFinderPage,
  '/app/key-insight': loadKeyInsightPage,
  '/app/product-database': loadProductDatabasePage,
  '/app/settings': loadSettingsPage,
  '/app/profile': loadProfilePage,
  '/app/new-release': loadNewReleasesPage,
  '/app/sales-estimator': loadSalesEstimatorPage,
  '/app/profit-calculator': loadProfitCalculatorPage,
  '/app/admin/allowed-emails': loadAdminAllowedEmailsPage,
  '/app/admin/anchor-review': loadAnchorReviewPage,
  '/app/admin/user-activity': loadUserActivityPage,
  '/app/admin/academy': loadAdminAcademyPage,
  '/app/admin/content': loadAdminContentManagementPage,
  '/app/admin/content/blog': loadAdminBlogManagementPage,
  '/app/admin/notifications': loadAdminNotificationsPage,
  '/app/admin/scraping-gate': loadAdminScrapingGatePage,
  '/app/admin/keyword-score-lab': loadAdminKeywordScoreLabPage,
  '/app/admin/extension-features': loadAdminExtensionFeaturesPage,
  '/app/academy': loadAcademyPage,
  '/app/keyword-lists': loadKeywordListsPage,
  '/app/listing-builder': loadListingBuilderPage,
  '/app/amazon/dashboard': loadAmazonDashboardPage,
  '/app/amazon/advertising': loadAdvertisingPage,
  '/app/billing': loadBillingPage,
  '/app/sqp': loadSQPPage,
  '/app/product-list': loadProductListPage,
}

/** Sayfa kodunu önden indirir. Hata yutulur: gerçek gezinmede lazy() yeniden dener. */
export function preloadPage(path: string): void {
  const load = loaders[path]
  if (load) void warmUp(load).catch(() => undefined)
}

// Sunucunun hazır HTML ile verdiği sayfalar (bkz. src/prerender/entry.tsx): çizim bunların kodu inene kadar bekler.
const prerenderedLoaders: Record<string, Loader> = {
  '/': loadLandingPage,
  '/about': loadAboutPage,
  '/contact': loadContactPage,
  '/pricing': loadPricingPage,
  '/privacy': loadPrivacyPolicyPage,
  '/terms': loadTermsPage,
}
// Hazır HTML'i olmayan diğer herkese açık sayfalar: kod paralel iner, çizim beklemez.
const otherPublicLoaders: Record<string, Loader> = {
  '/pricing/checkout': loadCheckoutPage,
  '/coming-soon/blog': loadBlogComingSoonPage,
  '/order-detail': loadOrderDetailPage,
  '/login': loadAuthPage,
  '/reset-password': loadResetPasswordPage,
  '/auth/callback': loadAuthCallbackPage,
  '/amazon/callback': loadAmazonOAuthCallbackPage,
  '/unauthorized': loadUnauthorizedPage,
}
/**
 * İlk çizimden önce açılış adresinin sayfa kodunu indirir. Sunucu bazı herkese açık sayfaları hazır HTML ile verir;
 * uygulama çizime başladığında bu HTML silinir. Sayfa kodu o an inmemişse içerik kaybolup geri gelirdi (boş an).
 * Bu sayfalarda kod önce iner, çizim sonra başlar: hazır HTML kod gelene kadar ekranda kalır. Hata ya da `timeoutMs`
 * aşımı çizimi engellemez (eksik dosya çizimden sonra gerçek gezinme yolunda yönetilir). Hazır HTML'i olmayan sayfalarda
 * (giriş, `/app/*`) kod yalnız paralel indirilir, çizim ve oturum istekleri beklemez.
 */
export function preloadInitialRoute(pathname: string, timeoutMs = 3000): Promise<void> {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  const isAlias = ALIAS_ROUTES.some((route) => route.path === path)
  const blocking = isAlias ? loadAliasPage : prerenderedLoaders[path]
  if (blocking) {
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
    return Promise.race([warmUp(blocking).then(() => undefined, () => undefined), timeout])
  }
  const background = otherPublicLoaders[path] ?? loaders[path]
  if (background) void warmUp(background).catch(() => undefined)
  return Promise.resolve()
}
